// Dropshipping In: how many units of each combination the Seller wants to sell.
// 6 <= quantity <= the Provider's quantity; every sale lowers both, a cancel/return gives
// both back; what buyers can order is min(Provider, your quantity). Empty = follow the Provider.
import React, { useCallback, useEffect, useState } from "react";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { isAuthenticated } from "../../auth";
import { getSellerQuantities, setSellerQuantity } from "../../dropshippingInterne/services/sellerQuantityService";
import "./SellerProviderQuantityPanel.css";

const comboLabel = (filter) => Object.entries(filter || {})
  .filter(([k]) => !k.startsWith("__"))
  .map(([k, v]) => `${k}: ${v}`).join(" · ") || text("h_sq_single", "Single option");

export default function SellerProviderQuantityPanel({ productId, onChanged }) {
  const token = isAuthenticated()?.token;
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    const r = await getSellerQuantities(token, productId);
    if (r?.success === false) return setState({ loading: false, error: r.error || "Error", data: null });
    setState({ loading: false, error: "", data: r });
    setDrafts(Object.fromEntries((r.rows || []).map((row) => [row.rowId, row.sellerQuantity ?? ""])));
  }, [token, productId]);
  useEffect(() => { if (productId) load(); }, [productId, load]);

  const save = async (row, value) => {
    setBusy(row.rowId); setNotice(null);
    const r = await setSellerQuantity(token, productId, row.rowId, value === "" ? null : Number(value));
    setBusy(null);
    if (r?.success === false) return setNotice({ type: "error", rowId: row.rowId, text: r.error });
    setState({ loading: false, error: "", data: r });
    setDrafts(Object.fromEntries((r.rows || []).map((x) => [x.rowId, x.sellerQuantity ?? ""])));
    setNotice({ type: "success", rowId: row.rowId, text: text("h_sq_saved", "Quantity saved.") });
    onChanged?.();
  };

  if (state.loading) return null;
  if (state.error) return <p className="text-danger" role="alert">{state.error}</p>;
  const { rows = [], min = 6, providerDeleted } = state.data || {};
  if (!rows.length) return null;

  return (
    <section className="sq-panel" aria-labelledby="sq-title" data-testid="seller-quantity-panel">
      <h4 id="sq-title" className="section-subtitle">{text("h_sq_title", "Quantity to sell")}</h4>
      <p className="sq-hint">{text("h_sq_hint", "Choose how many units of each combination you want to sell: at least {min}, never more than the Provider has. Each sale lowers your quantity and the Provider's; leave empty to sell everything the Provider has.", { min })}</p>
      {providerDeleted && <p className="sq-msg sq-msg--error" role="note">{text("h_ptr_unavailable_deleted", "Product unavailable — Deleted by Provider")}</p>}
      <div className="sq-table" role="table" aria-label={text("h_sq_title", "Quantity to sell")}>
        <div className="sq-row sq-row--head" role="row">
          <span role="columnheader">{text("h_sq_combination", "Combination")}</span>
          <span role="columnheader">{text("h_sq_provider_qty", "Provider quantity")}</span>
          <span role="columnheader">{text("h_sq_your_qty", "Your quantity")}</span>
          <span role="columnheader">{text("h_sq_sellable", "Buyers can order")}</span>
        </div>
        {rows.map((row) => {
          const draft = drafts[row.rowId] ?? "";
          const n = Number(draft);
          const invalid = draft !== "" && (!Number.isInteger(n) || n < min || n > row.providerQuantity);
          const unchanged = String(draft) === String(row.sellerQuantity ?? "");
          return (
            <div className="sq-row" role="row" key={row.rowId} data-testid="sq-row">
              <span role="cell" className="sq-combo">{comboLabel(row.filter)}</span>
              <span role="cell"><span className="sq-label">{text("h_sq_provider_qty", "Provider quantity")}</span>{row.providerQuantity.toLocaleString()}</span>
              <span role="cell" className="sq-edit">
                <span className="sq-label">{text("h_sq_your_qty", "Your quantity")}</span>
                {row.canSetQuantity && !providerDeleted ? (
                  <>
                    <input type="number" min={min} max={row.providerQuantity} step="1" inputMode="numeric" className="form-control"
                      aria-label={`${text("h_sq_your_qty", "Your quantity")} — ${comboLabel(row.filter)}`}
                      placeholder={text("h_sq_follow", "All (follow Provider)")} value={draft}
                      onChange={(e) => setDrafts((d) => ({ ...d, [row.rowId]: e.target.value }))} />
                    <button type="button" className="btn btn-sm btn-primary" disabled={busy === row.rowId || invalid || unchanged} onClick={() => save(row, draft)}>
                      {busy === row.rowId ? text("h_sq_saving", "Saving…") : text("h_sq_save", "Save")}
                    </button>
                    {row.sellerQuantity !== null && (
                      <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy === row.rowId} onClick={() => save(row, "")}>
                        {text("h_sq_follow_btn", "Follow Provider")}
                      </button>
                    )}
                  </>
                ) : (
                  <small className="text-muted">{providerDeleted ? "—" : text("h_sq_too_low", "The Provider has fewer than {min} units: you sell what the Provider has.", { min })}</small>
                )}
              </span>
              <span role="cell"><span className="sq-label">{text("h_sq_sellable", "Buyers can order")}</span><strong>{row.quantity.toLocaleString()}</strong></span>
              {invalid && <span className="sq-msg sq-msg--error" role="alert">{text("h_sq_range", "Enter a whole number between {min} and {max}.", { min, max: row.providerQuantity })}</span>}
              {notice?.rowId === row.rowId && <span className={`sq-msg sq-msg--${notice.type}`} role={notice.type === "error" ? "alert" : "status"}>{notice.text}</span>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
